import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import { useGeoPosition, PERMISSION_NAME_GEOLOCATION, PERMISSION_STATE_DENIED } from '@repo/geo-position';
import { toast } from '@repo/toast';
import { Button } from '@repo/ui';

import locationPermission from '@assets/images/parking-map/feature-location-permission.png';

interface Props {
  testId: TestId;
  onLater: () => void;
  className?: string;
}

export default function LocationPermissionModal({ testId, onLater, className = '' }: Props) {
  const { request: requestLocation } = useGeoPosition();

  const showDeniedToast = (): void => {
    toast.danger(tr('map:location_permission.denied'), testId.append('deniedToast'));
  };

  const handleEnableLocation = (): void => {
    requestLocation();
    navigator.permissions?.query({ name: PERMISSION_NAME_GEOLOCATION }).then((status) => {
      if (status.state === PERMISSION_STATE_DENIED) {
        showDeniedToast();
        return;
      }
      // Listen for active denial via the browser prompt.
      const onChange = () => {
        if (status.state === PERMISSION_STATE_DENIED) {
          showDeniedToast();
        }
        status.removeEventListener('change', onChange);
      };
      status.addEventListener('change', onChange);
    });
  };

  return (
    <div className={`flex flex-col gap-4 ${className}`} data-testid={testId}>
      <img src={locationPermission} alt={tr('map:location_permission.image_alt')} loading="lazy" className="mx-auto" />

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3 text-center">
          <div className="text-heading text-lg leading-7 font-semibold">{tr('map:location_permission.title')}</div>
          <div className="text-body text-base leading-6 font-normal">{tr('map:location_permission.description')}</div>
        </div>
        <div className="flex flex-col gap-3">
          <Button
            color="brand"
            variant="solid"
            size="lg"
            className="w-full"
            onClick={handleEnableLocation}
            testId={testId.append('enable')}
          >
            {tr('map:location_permission.enable_location')}
          </Button>
          <Button color="tertiary" size="lg" className="w-full" onClick={onLater} testId={testId.append('setLater')}>
            {tr('map:location_permission.set_later')}
          </Button>
        </div>
      </div>
    </div>
  );
}
